import { addDays, addMonths, setDate, startOfDay } from "date-fns";
import { NotificationSchedule } from "../types";

/**
 * "Próximo envio" não é devolvido pela API (o output só traz `last_run_at`),
 * então derivamos no front a partir da frequência.
 *
 * Limitações conhecidas — é uma estimativa para exibição, não a fonte da
 * verdade (quem dispara de fato é o EventBridge no backend):
 * - Ignoramos `timezone` do agendamento e usamos o fuso do navegador. Hoje a UI
 *   só oferece America/Sao_Paulo, então na prática coincide.
 * - Para `INTERVAL` não sabemos a âncora real da série quando nunca rodou; o
 *   backend ancora no primeiro `send_time` após a criação.
 */

type NextRunFields = Pick<
  NotificationSchedule,
  | "frequency_type"
  | "interval_days"
  | "cron_expr"
  | "send_time"
  | "status"
  | "last_run_at"
>;

const CRON_DAY_TO_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

function atSendTime(date: Date, sendTime: string): Date {
  const [hour, minute] = sendTime.split(":").map(Number);
  const result = startOfDay(date);
  result.setHours(hour, minute, 0, 0);
  return result;
}

/**
 * @param schedule agendamento vindo da API
 * @param from instante de referência — injetado para manter a função pura
 */
export function computeNextRun(
  schedule: NextRunFields,
  from: Date,
): Date | null {
  // Pausado não tem próximo envio.
  if (schedule.status === "PAUSED") return null;

  const { send_time: sendTime } = schedule;
  // Sem horário não há o que estimar. O tipo garante a presença, mas o block `schedule_list`
  // do chat é opaco (`z.record(z.unknown())`) e pode trazer o campo ausente — aqui isso
  // significaria derrubar a árvore do chat no meio do render.
  if (!sendTime) return null;
  const todayAtSendTime = atSendTime(from, sendTime);

  if (schedule.frequency_type === "INTERVAL") {
    const step = schedule.interval_days ?? 1;

    // Já rodou: conta a partir do último disparo.
    if (schedule.last_run_at) {
      const last = new Date(schedule.last_run_at);
      let next = atSendTime(addDays(last, step), sendTime);
      while (next <= from) next = addDays(next, step);
      return next;
    }

    // Nunca rodou: primeira ocorrência de send_time a partir de agora.
    return todayAtSendTime > from
      ? todayAtSendTime
      : addDays(todayAtSendTime, 1);
  }

  const fields = (schedule.cron_expr ?? "").trim().split(/\s+/);
  if (fields.length < 5) return null;
  const [, , dayOfMonth, month, dayOfWeek] = fields;

  // Semanal: próximo dia da semana marcado.
  if (dayOfWeek && dayOfWeek !== "*") {
    const targets = dayOfWeek
      .split(",")
      .map((day) => CRON_DAY_TO_INDEX[day.trim().toUpperCase()])
      .filter((index): index is number => index !== undefined);

    if (!targets.length) return null;

    for (let offset = 0; offset <= 7; offset += 1) {
      const candidate = atSendTime(addDays(from, offset), sendTime);
      if (candidate > from && targets.includes(candidate.getDay())) {
        return candidate;
      }
    }
    return null;
  }

  // Mensal/trimestral: dia 1, eventualmente restrito a certos meses.
  if (dayOfMonth && dayOfMonth !== "*") {
    const allowedMonths =
      month && month !== "*"
        ? month.split(",").map((value) => Number(value.trim()) - 1)
        : null;

    let candidate = atSendTime(setDate(from, Number(dayOfMonth)), sendTime);

    for (let attempt = 0; attempt <= 12; attempt += 1) {
      const monthAllowed =
        !allowedMonths || allowedMonths.includes(candidate.getMonth());
      if (candidate > from && monthAllowed) return candidate;
      candidate = atSendTime(
        setDate(addMonths(candidate, 1), Number(dayOfMonth)),
        sendTime,
      );
    }
  }

  return null;
}
